import { useState } from "react";

import { MapPin } from "lucide-react";

import { RemoteSelect } from "@/components/fields/remote-select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { CityCenter } from "@/pages/tina/items/components/panels/hitl-panel/city-center";
import type { VenueOption } from "@/pages/tina/items/components/panels/hitl-panel/refresh-resolution.helpers";
import type { RefreshVenueAction } from "@/types/tina";

import { cityMapsUrl, isGoogleMapsUrl } from "./venue-resolution.helpers";

type Mode = "pick" | "maps" | "use_maps";

interface GeodataOption {
  id: string;
  name: string;
  countryCode?: string;
}

/**
 * The scrape venue HITL's three ways out, as a controlled field group for a refresh review's
 * `venue` proposal (2026-09-11): pick a venue Mood already has, create one from a Google Maps
 * link, or use the link as the location with no venue record.
 *
 * `venue-resolution.tsx` owns the same three modes for the scrape HITL, plus a fourth
 * (confirming a stashed Places candidate) and a nearest-cities strip, both of which need a
 * `venue_geodata_miss` context a refresh park never has — so this is the subset that fits a
 * proposal row, and it EMITS an action rather than submitting one: the row's Submit button
 * belongs to the whole review. `onChange(undefined)` means "nothing writable yet" (no id
 * picked, no valid Maps URL), which is what keeps Submit disabled.
 */
export function VenueActionFields({
  pageVenueText,
  venueOption,
  onChange,
  search,
  searchGeodata,
  cityCenter,
}: {
  /** The page's own "name, address" text, shown so the curator knows what they are placing. */
  pageVenueText: string;
  /** The picked venue, kept only so `RemoteSelect` can render the selection. */
  venueOption?: VenueOption;
  onChange: (
    action: RefreshVenueAction | undefined,
    option?: VenueOption,
  ) => void;
  search: (query: string) => Promise<unknown[]>;
  searchGeodata: (query: string) => Promise<unknown[]>;
  /** The city offered by the "use this city's center" autofill button, already
   * resolved by the caller — undefined hides the button. */
  cityCenter?: CityCenter;
}) {
  const [mode, setMode] = useState<Mode>("pick");
  const [mapsUrl, setMapsUrl] = useState("");
  const [geodata, setGeodata] = useState<GeodataOption | undefined>();

  const mapsMode = mode !== "pick";
  const mapsUrlValid = isGoogleMapsUrl(mapsUrl);
  const mapsUrlInvalid = mapsMode && mapsUrl.trim().length > 0 && !mapsUrlValid;

  // TBA-venue helper: same idea as `venue-resolution.tsx`'s button. Nothing to look
  // up here — the caller resolved the city from the published event's pin — so this
  // only writes the two fields the curator would otherwise fill by hand.
  function handleAutofillCity() {
    if (!cityCenter) return;
    const geo: GeodataOption = {
      id: cityCenter.geodataId,
      name: cityCenter.name,
      countryCode: cityCenter.countryCode,
    };
    const url = cityMapsUrl(cityCenter.lat, cityCenter.lng);
    setGeodata(geo);
    setMapsUrl(url);
    emitMaps("use_maps", url, geo);
  }

  function emitMaps(
    nextMode: Mode,
    url: string,
    geo: GeodataOption | undefined,
  ) {
    if (nextMode === "pick") return;
    if (!isGoogleMapsUrl(url)) {
      onChange(undefined);
      return;
    }
    const base = {
      maps_url: url.trim(),
      ...(geo?.id ? { geodata_id: geo.id } : {}),
    };
    onChange(
      nextMode === "use_maps"
        ? { kind: "venue.use_maps_url", ...base }
        : { kind: "venue.create_from_maps", ...base },
    );
  }

  function switchMode(next: Mode) {
    setMode(next);
    if (next === "pick") {
      onChange(
        venueOption
          ? { kind: "venue.pick", venue_id: venueOption.id }
          : undefined,
        venueOption,
      );
    } else {
      emitMaps(next, mapsUrl, geodata);
    }
  }

  const modes: ReadonlyArray<{ mode: Mode; label: string }> = [
    { mode: "pick", label: "Use an existing venue" },
    { mode: "maps", label: "Create from a Google Maps link" },
    {
      mode: "use_maps",
      label: "Use this Google Maps link as the location (no venue)",
    },
  ];

  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs text-muted-foreground">
        Page&apos;s venue text:{" "}
        <span className="font-mono">{pageVenueText}</span>
      </span>

      <div className="flex flex-wrap gap-1">
        {modes.map(({ mode: m, label }) => (
          <Button
            key={m}
            type="button"
            size="sm"
            variant={mode === m ? "default" : "outline"}
            onClick={() => switchMode(m)}
          >
            {label}
          </Button>
        ))}
      </div>

      {mode === "pick" && (
        <RemoteSelect<VenueOption>
          value={venueOption}
          triggerType="change"
          showSelected
          onValueChanged={(selected) =>
            onChange(
              selected
                ? { kind: "venue.pick", venue_id: selected.id }
                : undefined,
              selected,
            )
          }
          accessorFn={(option) => ({
            id: option?.id || "",
            name: option
              ? `${option.name}${option.address ? ` (${option.address})` : ""}`
              : "",
            iconUrl: option?.media?.photos?.[0]?.url,
          })}
          onSearch={async (query, onStatusChanged) => {
            onStatusChanged("loading");
            const results = (await search(query)) as VenueOption[];
            onStatusChanged("success");
            return results;
          }}
        />
      )}

      {mapsMode && (
        <div className="flex flex-col gap-1">
          {mode === "use_maps" && cityCenter && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="w-fit"
              onClick={handleAutofillCity}
            >
              <MapPin className="mr-1 h-4 w-4" />
              Use {cityCenter.name} city center (TBA venue)
            </Button>
          )}
          <Input
            aria-label="Google Maps link"
            value={mapsUrl}
            onChange={(event) => {
              setMapsUrl(event.target.value);
              emitMaps(mode, event.target.value, geodata);
            }}
            placeholder="https://maps.app.goo.gl/…"
          />
          <span className="text-xs text-muted-foreground">
            {mode === "use_maps"
              ? "Attaches the link to the event as its location — the venue record is cleared, no venue is created."
              : "Short links (maps.app.goo.gl) are expanded server-side; an existing Mood venue at that place is reused."}
          </span>
          {mapsUrlInvalid && (
            <span className="text-xs font-medium text-destructive">
              That&apos;s not a Google Maps link. Paste a maps.app.goo.gl short
              link or a google.com/maps URL.
            </span>
          )}
          <span className="text-sm font-medium">City / geodata (optional)</span>
          <RemoteSelect<GeodataOption>
            value={geodata}
            triggerType="change"
            showSelected
            onValueChanged={(selected) => {
              setGeodata(selected);
              emitMaps(mode, mapsUrl, selected);
            }}
            accessorFn={(option) => ({
              id: option?.id || "",
              name: option
                ? `${option.name}${option.countryCode ? ` (${option.countryCode})` : ""}`
                : "",
            })}
            onSearch={async (query, onStatusChanged) => {
              onStatusChanged("loading");
              const results = (await searchGeodata(query)) as GeodataOption[];
              onStatusChanged("success");
              return results;
            }}
          />
          <span className="text-xs text-muted-foreground">
            Optional: pick a city only to override auto-detection.
          </span>
        </div>
      )}
    </div>
  );
}
